import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { ArrowUpRight } from 'lucide-react'
import { type MetaFunction, Link } from 'react-router'
import { serverOnly$ } from 'vite-env-only/macros'
import { SmoothScroller } from '#app/components/smooth-scroller.tsx'
import { getDomainUrl } from '#app/utils/misc.tsx'
import { type Route } from './+types/index'

const title = 'Artículos jurídicos sobre derecho en Costa Rica'
const description =
	'Análisis, guías y criterios de IUS PRO sobre derecho costarricense para abogados, bufetes y equipos legales: normativa, jurisprudencia, práctica profesional y el uso de la inteligencia artificial en el ejercicio del derecho.'

type ArticleSummary = {
	slug: string
	title: string
	description: string
	tag: string
}

const articles: ArticleSummary[] = [
	{
		slug: 'porque-no-deberias-usar-chatgpt-para-consultas-legales-en-costa-rica',
		title: 'Por qué no deberías usar ChatGPT para consultas legales en Costa Rica',
		description:
			'ChatGPT redacta y ordena ideas con rapidez, pero una consulta legal costarricense exige fuentes verificables, normativa vigente y criterio jurídico local.',
		tag: 'ChatGPT',
	},
	{
		slug: 'porque-no-deberias-usar-claude-para-consultas-legales-en-costa-rica',
		title: 'Por qué no deberías usar Claude para consultas legales en Costa Rica',
		description:
			'Claude razona y escribe a un nivel notable, pero razonar bien sobre una base equivocada no sustituye el derecho costarricense con fuentes comprobables.',
		tag: 'Claude',
	},
	{
		slug: 'porque-no-deberias-usar-copilot-de-microsoft-para-consultas-legales-en-costa-rica',
		title: 'Por qué no deberías usar Copilot de Microsoft para consultas legales en Costa Rica',
		description:
			'Copilot integra IA en Word, Outlook y la oficina, pero estar dentro de Office no lo convierte en una fuente confiable de derecho de Costa Rica.',
		tag: 'Copilot',
	},
]

export const handle: SEOHandle = {
	getSitemapEntries: serverOnly$(async () => [
		{
			route: '/articulos',
			priority: 0.7,
			changefreq: 'weekly',
		},
	]),
}

export async function loader({ request }: Route.LoaderArgs) {
	return {
		canonicalUrl: `${getDomainUrl(request)}/articulos`,
	}
}

export const meta: MetaFunction<typeof loader> = ({ loaderData }) => {
	const canonicalUrl = loaderData?.canonicalUrl

	return [
		{ title: `${title} | IUS PRO` },
		{ name: 'description', content: description },
		{ name: 'og:title', content: title },
		{ name: 'og:description', content: description },
		{ name: 'og:type', content: 'website' },
		...(canonicalUrl
			? [
					{ name: 'og:url', content: canonicalUrl },
					{ tagName: 'link', rel: 'canonical', href: canonicalUrl },
				]
			: []),
	]
}

export default function ArticlesIndex() {
	return (
		<div className="theme-catpuccin min-h-[100dvh] bg-app-background text-app-foreground">
			<SmoothScroller />
			<header className="border-b border-app-border bg-[#fefefe] pt-24">
				<div className="mx-auto max-w-6xl px-5 pb-14 pt-10 lg:px-8 lg:pb-16 lg:pt-14">
					<nav className="flex items-center gap-2 text-sm text-muted-foreground" aria-label="Breadcrumb">
						<Link to="/" className="font-medium text-foreground transition-colors hover:text-accent">
							IUS PRO
						</Link>
						<span className="text-app-border">/</span>
						<span>Artículos</span>
					</nav>

					<div className="mt-9 max-w-4xl">
						<span className="inline-flex items-center rounded-full border border-neutral-200 bg-white/85 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-neutral-700 shadow-sm">
							Costa Rica
						</span>
						<h1 className="mt-6 text-balance text-4xl font-light leading-[1.04] tracking-tighter text-neutral-950 sm:text-5xl lg:text-6xl">
							{title}
						</h1>
						<p className="mt-6 max-w-3xl text-pretty text-lg leading-9 text-neutral-500 sm:text-xl">{description}</p>
					</div>
				</div>
			</header>

			<div className="mx-auto max-w-6xl px-5 pb-24 pt-12 lg:px-8 lg:pt-14">
				<ul className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
					{articles.map(article => (
						<li key={article.slug}>
							<Link
								to={`/articulos/${article.slug}`}
								prefetch="intent"
								className="group flex h-full flex-col justify-between border border-neutral-200 bg-white p-6 shadow-[0_18px_50px_rgba(23,23,23,0.06)] transition-colors hover:border-neutral-400"
							>
								<div>
									<span className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500">
										{article.tag}
									</span>
									<h2 className="mt-4 text-pretty text-xl font-light leading-snug tracking-tight text-neutral-950">
										{article.title}
									</h2>
									<p className="mt-4 text-pretty text-sm leading-7 text-neutral-500">{article.description}</p>
								</div>
								<span className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-neutral-900">
									Leer artículo
									<ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
								</span>
							</Link>
						</li>
					))}
				</ul>
			</div>
		</div>
	)
}
